import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import {
  ContributorsScheduleDocument,
  ContributorsScheduleQuery,
  ProjectScheduleEmployeeFragment,
  ProjectScheduleProjectWorkerFragment,
} from 'generated/resources/graphql'
import moment, { Moment } from 'moment'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { ScheduleParseDataType } from 'modules/project_management/components/Schedule/Body'
import { ScheduleRecordType } from 'modules/project_management/components/Schedule/Body/ScheduleItem/types'
import { FooterElement } from 'modules/project_management/components/Schedule/Footer/index'
import Schedule from 'modules/project_management/components/Schedule/index'
import { ScheduleOnboardingHint } from 'modules/project_management/components/hints/schedule/ScheduleOnboarding'

const ContributorsSchedule = () => {
  const [hideBetaBanner, setHideBetaBanner] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideContributorsScheduleBetaBanner
  )

  useEffect(() => {
    track(trackingEvents.projectManagement.schedule.viewSchedulePage, {})
  }, [])

  const dataSource = (data: ContributorsScheduleQuery) => {
    const connection = data?.projectManagement.projectContributorsConnection
    return {
      edges:
        connection?.edges.map((edge) => ({
          cursor: edge.cursor ?? '',
          node: edge.node,
        })) ?? [],
      pageInfo: {
        hasNextPage: connection?.pageInfo.hasNextPage ?? false,
        endCursor: connection?.pageInfo.endCursor ?? '',
      },
    }
  }

  const parseData = (
    data: ContributorsScheduleQuery | undefined,
    startDate: Moment,
    endDate: Moment
  ): ScheduleParseDataType => {
    const transformRecords = (
      projectWorker: ProjectScheduleProjectWorkerFragment,
      employee: EmployeeForSelectFragmentNoAccess
    ): ScheduleRecordType => {
      return projectWorker.plannedRecords.reduce<ScheduleRecordType>((acc, record) => {
        const key = record.subproject?.id ? record.subproject?.id?.toString() : 'null'

        if (!acc[key]) {
          acc[key] = []
        }

        if (
          projectWorker.project &&
          moment(record.startDate).isBefore(endDate) &&
          moment(record.endDate).isAfter(startDate)
        ) {
          acc[key].push({
            id: record.id,
            startDate: record.startDate,
            endDate: record.endDate,
            dailyMinutes: record.dailyMinutes,
            group: projectWorker.project?.name ?? '',
            title: record.subproject ? record.subproject.name : null,
            subtitle: projectWorker.project?.client?.legalName ?? null,
            projectWorkerId: projectWorker.id,
            project: projectWorker.project ?? undefined,
            subproject: record.subproject ?? undefined,
            employee: employee,
          })
        }

        if (acc[key].length === 0) {
          delete acc[key]
        }

        return acc
      }, {})
    }
    return {
      items:
        data?.projectManagement.projectContributorsConnection.edges
          .filter((edge) => edge.node.employee)
          .flatMap(({ node: contributor }) => {
            if (!contributor.employee) return []

            const employee: ProjectScheduleEmployeeFragment = contributor.employee

            const employeeToSelect = employeeToSelectFragment(employee)

            return {
              headerRow: {
                id: parseInt(employee.id.toString() || '0'),
                fullName: employee.fullName || '',
                avatar: employee.avatar?.url || '',
                jobTitle: employee.referenceContract?.jobTitle || '',
              },
              employeeToSelect: employeeToSelect,
              rows: contributor.projectWorkers
                .map((pw: ProjectScheduleProjectWorkerFragment) => {
                  const records = transformRecords(pw, employeeToSelect)
                  if (Object.keys(records).length === 0) return null

                  return {
                    firstColumn: {
                      id: pw.project?.id.toString() || '',
                      title: pw.project?.name || '',
                      subtitle: pw.project?.client?.legalName || '',
                    },
                    records: records,
                  }
                })
                .filter((row) => row !== null),
            }
          }) || [],
      totalCount: data?.projectManagement.projectContributorsConnection.totalCount || 0,
    }
  }

  const employeeToSelectFragment = (
    employee: ProjectScheduleEmployeeFragment
  ): EmployeeForSelectFragmentNoAccess => {
    return {
      id: employee.id,
      fullName: employee.fullName,
      firstName: employee.firstName,
      lastName: employee.lastName,
      access: { avatar: null },
    }
  }

  const footerElementsList: FooterElement[] = [
    {
      color: 'purple200',
      circleType: 'circle',
      description: i18n.t(
        'project_management.schedule_contributors.footer_legend.total_assigned_hours'
      ),
    },
    {
      color: 'purple1000',
      circleType: 'circle-line',
      description: i18n.t(
        'project_management.schedule_contributors.footer_legend.detailed_hours_per_project'
      ),
    },
  ]

  return (
    <>
      {!hideBetaBanner && (
        <Box paddingX="s24" paddingY="s8">
          <Box
            background="viridian100"
            flexDirection="row"
            gap="s8"
            paddingY="s12"
            paddingX="s16"
            borderRadius={{ all: 'abs016' }}
            justifyContent="spaceBetween"
          >
            <Box flexDirection="row" gap="s8">
              <Icon.Medium icon="lightbulb" color="viridian1000" />
              <Text.Normal color="grey1000">
                {i18n.t('project_management.schedule.banner.title')}
              </Text.Normal>
            </Box>
            <Box alignSelf="center" onClick={() => setHideBetaBanner(true)} cursor="pointer">
              <Icon.Medium icon="cross" color="viridian1000" />
            </Box>
          </Box>
        </Box>
      )}
      <ScheduleOnboardingHint />

      <Schedule
        document={ContributorsScheduleDocument}
        documentVariables={{ employeeIds: [], startDate: '2025-03-01', endDate: '2025-03-29' }}
        dataSource={dataSource}
        parseData={parseData}
        footerLegend={footerElementsList}
        showBetaTag={!!hideBetaBanner}
      />
    </>
  )
}

export default ContributorsSchedule
